class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    this.updateFreeShippingProgress();
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.applyLayoutConstraints();
      this.resetScrollPosition();
      this.updateFreeShippingProgress();
      this.open();
    });
  }

  applyLayoutConstraints() {
    const drawerInner = this.querySelector('.drawer__inner');
    const drawerItems = this.querySelector('cart-drawer-items');

    if (drawerInner) {
      drawerInner.style.display = 'grid';
      drawerInner.style.gridTemplateRows = 'auto auto minmax(0, 1fr) auto';
      drawerInner.style.overflow = 'hidden';
    }

    if (drawerItems) {
      drawerItems.style.minHeight = '0';
      drawerItems.style.overflow = 'auto';
    }
  }

  resetScrollPosition() {
    [
      this.querySelector('.drawer__inner'),
      this.querySelector('cart-drawer-items'),
      this.querySelector('#CartDrawer'),
    ].forEach((element) => {
      if (element) element.scrollTop = 0;
    });
  }

  updateFreeShippingProgress() {
    const freeShipping = this.querySelector('[data-cart-free-shipping]');
    if (!freeShipping) return;

    const thresholdUsdCents = Number(freeShipping.dataset.thresholdUsdCents || 0);
    const cartTotalCents = Number(freeShipping.dataset.cartTotalCents || 0);
    const currencyRate = Number(Shopify.currency?.rate || 1);
    const activeCurrency = Shopify.currency?.active || 'USD';
    const thresholdCents = Math.round(thresholdUsdCents * currencyRate);
    const remainingCents = thresholdCents - cartTotalCents;
    const progress = thresholdCents > 0 ? Math.min((cartTotalCents / thresholdCents) * 100, 100) : 0;
    const progressElement = freeShipping.querySelector('.cart-drawer__free-shipping-progress');
    const messageElement = freeShipping.querySelector('.cart-drawer__free-shipping-message');

    if (progressElement) progressElement.style.setProperty('--cart-free-shipping-progress', `${progress}%`);
    if (!messageElement) return;

    if (remainingCents > 0) {
      messageElement.innerHTML = `You are <span data-free-shipping-remaining>${this.formatMoney(
        remainingCents,
        activeCurrency
      )}</span> away from <strong>free shipping</strong>.`;
    } else {
      messageElement.innerHTML = 'Your order qualifies for <strong>free shipping</strong>.';
    }
  }

  formatMoney(cents, currency = Shopify.currency?.active || 'USD') {
    const amount = Number(cents) / 100;
    return new Intl.NumberFormat(Shopify.locale || 'en', {
      style: 'currency',
      currency,
    }).format(amount);
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  connectedCallback() {
    super.connectedCallback();
    requestAnimationFrame(() => {
      document.querySelector('cart-drawer')?.updateFreeShippingProgress();
    });
  }

  disableLoading(line) {
    super.disableLoading(line);
    requestAnimationFrame(() => {
      document.querySelector('cart-drawer')?.updateFreeShippingProgress();
    });
  }

  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
